import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatDate } from '@/lib/dates';
import { MFA_ACTIVATION_DATE } from '@/components/blocking-phone/constants';

/** Coordonnées du courtier (`contracts.data[n].apporteurs.apporteur1`). */
export interface BrokerApporteur {
  email_principal?: string | null;
  phone?: string | null;
  [key: string]: unknown;
}

// Popin bloquante des clients courtiers : depuis l'activation de la double
// authentification, un code SMS est envoyé à chaque connexion. Le client
// courtier ne peut pas saisir son numéro lui-même (contrairement au direct et
// aux courtiers self-service), il doit passer par son courtier — la popin est
// donc non fermable, seule issue = contacter son courtier.
const formatPhone = (phone: string) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 10) return (digits.match(/.{2}/g) ?? []).join('.');
  return phone;
};

const BrokerMfaPopup = ({ apporteur }: { apporteur: BrokerApporteur | null }) => {
  const brokerEmail = apporteur?.email_principal || null;
  const brokerPhone = apporteur?.phone || null;
  const brokerPhoneDigits = (brokerPhone || '').replace(/\D/g, '');

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-12 bg-[rgba(0,30,49,0.55)] backdrop-blur-[4px]"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="broker-mfa-title"
    >
      <div className="relative w-full max-w-[440px] bg-card rounded-[18px] shadow-[0_24px_60px_rgba(0,40,65,0.35)] px-[30px] pt-8 pb-6 text-center animate-[slideUp_0.25s_ease]">
        <div
          className="w-16 h-16 mx-auto mb-6 rounded-[18px] flex items-center justify-center shadow-[0_8px_20px_rgba(62,170,196,0.35)]"
          style={{ background: 'linear-gradient(150deg,#3EAAC4,#003C61)' }}
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect x="6" y="2" width="12" height="20" rx="3" stroke="#fff" strokeWidth="1.8" />
            <path d="M10 19h4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="18" cy="6" r="4.5" fill="#F8AA36" stroke="#fff" strokeWidth="1.5" />
            <path d="M16.3 6l1.2 1.2 2.2-2.4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <span className="inline-block text-[10px] font-bold tracking-[0.6px] uppercase text-white bg-[#F8AA36] px-[11px] py-1 rounded-[20px] mb-4">
          Depuis le {formatDate(MFA_ACTIVATION_DATE, 'dd/MM/yyyy')}
        </span>
        <h2 id="broker-mfa-title" className="text-xl font-semibold text-primary leading-[1.3] mb-4">
          La double authentification est active
        </h2>
        <p className="text-sm text-muted-foreground leading-[1.6] mb-2">
          Pour renforcer la sécurité de votre espace, un{' '}
          <strong className="text-foreground font-semibold">code par SMS</strong> vous est
          envoyé à chaque connexion.
        </p>

        <div className="flex items-center gap-3 text-left bg-[rgba(62,170,196,0.08)] border border-[rgba(62,170,196,0.22)] rounded-xl px-[15px] py-[13px] mt-[18px] mb-[22px]">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="#3EAAC4" fillOpacity="0.15" />
            <path d="M12 7v6" stroke="#003C61" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="12" cy="16.5" r="1.1" fill="#003C61" />
          </svg>
          <span className="text-[13px] text-foreground leading-[1.45]">
            Votre <b className="text-primary">numéro de mobile</b> est absent de votre
            dossier&nbsp;!
            <br />
            Contactez votre courtier pour qu’il l’ajoute et retrouver l’accès à votre espace.
          </span>
        </div>

        {(brokerEmail || brokerPhone) && (
          <div className="w-full bg-[#F6F7F9] border border-[#E4E8EC] rounded-[9px] px-6 py-3.5 text-left mb-2.5">
            <p className="text-sm font-semibold text-primary mb-1.5">
              Contactez mon courtier&nbsp;:
            </p>
            {brokerEmail && (
              <p className="text-[13px] text-muted-foreground leading-[1.6]">
                Email&nbsp;:{' '}
                <a href={`mailto:${brokerEmail}`} className="font-medium text-foreground hover:text-primary break-all">
                  {brokerEmail}
                </a>
              </p>
            )}
            {brokerPhone && (
              <p className="text-[13px] text-muted-foreground leading-[1.6]">
                Téléphone&nbsp;:{' '}
                <a href={`tel:${brokerPhoneDigits}`} className="font-medium text-foreground hover:text-primary">
                  {formatPhone(brokerPhone)}
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default BrokerMfaPopup;
